import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { FileText, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Dialog shown right before an application is submitted.
 * Lets the seeker pick between:
 *   (a) their profile default resume (if any), or
 *   (b) uploading a fresh resume just for this application.
 *
 * The freshly-uploaded resume is stored on the application document only —
 * it never replaces the seeker's default profile resume.
 */
export default function ApplyResumeChoiceDialog({
  open, onOpenChange, jobId, jobTitle, onApplied,
}) {
  const [profileResume, setProfileResume] = useState(null); // {filename, uploaded_at}
  const [pickedFile, setPickedFile] = useState(null);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPickedFile(null);
    setChecking(true);
    api.get("/profile/seeker")
      .then(({ data }) => {
        if (data && data.resume_filename) {
          setProfileResume({ filename: data.resume_filename, uploaded_at: data.resume_uploaded_at });
          setMode("existing");
        } else {
          setProfileResume(null);
          setMode("new");
        }
      })
      .catch(() => setProfileResume(null))
      .finally(() => setChecking(false));
  }, [open]);

  const pickFile = () => {
    setMode("new");
    inputRef.current?.click();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Only PDF, DOC or DOCX files allowed");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Resume too large — max 5 MB");
      return;
    }
    setPickedFile(f);
    setMode("new");
  };

  const submit = async () => {
    if (mode === "new" && !pickedFile) {
      toast.error("Please choose a resume file to upload");
      return;
    }
    setSubmitting(true);
    try {
      const refParam = new URLSearchParams(window.location.search).get("ref");
      const hostParam = new URLSearchParams(window.location.search).get("host");
      const headers = { "Content-Type": "multipart/form-data" };
      if (refParam) headers["X-QaTest-Source"] = refParam;
      if (hostParam) headers["X-QaTest-Source-Host"] = hostParam;

      const form = new FormData();
      if (mode === "new" && pickedFile) form.append("resume", pickedFile);
      const { data } = await api.post(`/jobs/${jobId}/apply`, form, { headers });
      toast.success("Application submitted!");
      onApplied?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to apply");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="apply-resume-choice-dialog">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 grid place-items-center text-[#002FA7] mb-2">
            <FileText className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center font-display text-2xl font-black tracking-tight">
            Which resume should we send?
          </DialogTitle>
          <DialogDescription className="text-center text-zinc-600 pt-1">
            Applying to <span className="font-semibold text-zinc-900">{jobTitle}</span>. Choose one below —
            a new upload here won't replace your default profile resume.
          </DialogDescription>
        </DialogHeader>

        {checking ? (
          <div className="py-6 flex items-center justify-center text-sm text-zinc-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-[#002FA7]" /> Loading your profile…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Existing profile resume */}
            <button
              type="button"
              onClick={() => profileResume && setMode("existing")}
              disabled={!profileResume}
              data-testid="resume-choice-existing"
              className={`w-full text-left rounded-md border p-4 transition-colors ${
                mode === "existing" && profileResume
                  ? "border-[#002FA7] bg-blue-50"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              } ${!profileResume ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-start gap-3">
                <span className={`h-9 w-9 rounded-md grid place-items-center flex-shrink-0 ${
                  mode === "existing" && profileResume
                    ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-500"
                }`}>
                  {mode === "existing" && profileResume
                    ? <CheckCircle2 className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-950 flex items-center gap-2">
                    Use my profile resume
                  </div>
                  {profileResume ? (
                    <div className="text-xs text-zinc-500 tag-mono truncate mt-0.5">
                      {profileResume.filename}
                      {profileResume.uploaded_at ? ` · uploaded ${profileResume.uploaded_at.slice(0,10)}` : ""}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-700 tag-mono mt-0.5">
                      No resume on your profile yet — upload one below.
                    </div>
                  )}
                </div>
              </div>
            </button>

            {/* New/temporary resume */}
            <div
              className={`w-full rounded-md border transition-colors ${
                mode === "new"
                  ? "border-[#002FA7] bg-blue-50"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
            >
              <button
                type="button"
                onClick={pickFile}
                data-testid="resume-choice-new"
                className="w-full text-left p-4"
              >
                <div className="flex items-start gap-3">
                  <span className={`h-9 w-9 rounded-md grid place-items-center flex-shrink-0 ${
                    mode === "new" ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-500"
                  }`}>
                    {mode === "new" && pickedFile
                      ? <CheckCircle2 className="h-5 w-5" /> : <Upload className="h-5 w-5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-950">Upload a new resume for this job</div>
                    {pickedFile ? (
                      <div className="text-xs text-emerald-700 tag-mono truncate mt-0.5">
                        {pickedFile.name} · {(pickedFile.size / 1024).toFixed(0)} KB
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500 tag-mono mt-0.5">
                        Click to browse · PDF · DOC · DOCX · max 5 MB — used only for this application.
                      </div>
                    )}
                  </div>
                </div>
              </button>
              {mode === "new" && (
                <div className="border-t border-zinc-200 px-4 py-2 flex items-center justify-between gap-2 bg-white/60">
                  <span className="text-[11px] tag-mono text-zinc-500">
                    {pickedFile ? "Ready to submit" : "No file chosen yet"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={pickFile}
                      data-testid="resume-choice-pick-different"
                      className="text-xs font-medium text-[#002FA7] hover:text-[#00227A] px-2 py-1 rounded hover:bg-blue-50"
                    >
                      {pickedFile ? "Choose a different file" : "Browse files"}
                    </button>
                    {pickedFile && (
                      <button
                        type="button"
                        onClick={() => setPickedFile(null)}
                        data-testid="resume-choice-clear-file"
                        className="text-xs font-medium text-zinc-500 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onFile}
              className="hidden"
              data-testid="resume-choice-file-input"
            />
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || checking || (mode === "existing" && !profileResume) || (mode === "new" && !pickedFile)}
            data-testid="resume-choice-submit"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
          >
            {submitting ? "Submitting…" : "Submit application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
